/**
 * Status — live swap-route health.
 *
 * Shows which "pay with any crypto" coins are routable to settlement right
 * now, straight from the backend's route-health monitor (probes LeoKit
 * /quote for every supported coin → Base USDC at startup + every 5 min).
 * The checkout hides coins with no live route, so this tab is the merchant's
 * window into what their customers can currently pay with — and a heads-up
 * when a coin is temporarily down.
 *
 * Read-only, public endpoint; auto-refreshes every 30s.
 */

import { useCallback, useEffect, useState } from "react";
import {
  getRouteHealth,
  type RouteHealth,
  type RouteHealthEntry,
} from "../lib/api";
import { Banner, Btn } from "../components/ui";

const REFRESH_MS = 30_000;

const PROTOCOL_LABEL: Record<string, string> = {
  near: "NEAR Intents",
  chainflip: "Chainflip",
  "relay-deposit": "Relay",
  relay: "Relay",
};

type RouteState = "live" | "recovering" | "down";

/**
 * Map the raw monitor fields to a single merchant-facing state.
 * `offered` is the gate the checkout uses (hysteresis applied); `healthy`
 * is the raw last sweep. A coin that just missed one probe is still offered,
 * so we surface that as "recovering" rather than alarming "down".
 */
const stateOf = (r: RouteHealthEntry): RouteState => {
  if (!r.offered) return "down";
  if (!r.healthy) return "recovering";
  return "live";
};

const STATE_META: Record<
  RouteState,
  { pill: string; label: string; rank: number }
> = {
  // down first so a problem is the first thing seen; live last (calm default).
  down: { pill: "pill fail", label: "Down", rank: 0 },
  recovering: { pill: "pill warn", label: "Recovering", rank: 1 },
  live: { pill: "pill ok", label: "Live", rank: 2 },
};

const formatRelative = (t: number): string => {
  if (!t) return "—";
  const delta = Date.now() - t;
  const min = 60_000;
  const hour = 3_600_000;
  if (delta < 5_000) return "just now";
  if (delta < min) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < hour) return `${Math.floor(delta / min)}m ago`;
  return `${Math.floor(delta / hour)}h ago`;
};

export function Status() {
  const [data, setData] = useState<RouteHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent: boolean) => {
    if (silent) setRefreshing(true);
    try {
      const r = await getRouteHealth();
      setData(r);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load(false);
    const id = setInterval(() => void load(true), REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  const routes = data?.routes ?? [];
  const sorted = [...routes].sort((a, b) => {
    const ra = STATE_META[stateOf(a)].rank;
    const rb = STATE_META[stateOf(b)].rank;
    if (ra !== rb) return ra - rb;
    return a.label.localeCompare(b.label);
  });

  const counts = sorted.reduce(
    (acc, r) => {
      acc[stateOf(r)] += 1;
      return acc;
    },
    { live: 0, recovering: 0, down: 0 } as Record<RouteState, number>,
  );

  const warming = !loading && !error && (data?.updated_at ?? 0) === 0;

  return (
    <div className="scroll">
      <div className="pad fade-in">
        {error && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="fail">{error}</Banner>
          </div>
        )}

        {/* Summary — emerald only when everything is operational. */}
        {!loading && !error && !warming && routes.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            {counts.down > 0 ? (
              <Banner tone="warn" icon="warn">
                {counts.down} payment {counts.down === 1 ? "route is" : "routes are"}{" "}
                temporarily unavailable. Customers won't see{" "}
                {counts.down === 1 ? "this coin" : "these coins"} at checkout
                until {counts.down === 1 ? "it recovers" : "they recover"} — your
                other routes are unaffected.
              </Banner>
            ) : counts.recovering > 0 ? (
              <Banner tone="warn" icon="clock">
                {counts.recovering}{" "}
                {counts.recovering === 1 ? "route is" : "routes are"} recovering
                from a brief network hiccup. Still offered to customers.
              </Banner>
            ) : (
              <Banner tone="ok" icon="check">
                All {counts.live} payment routes operational. Customers can pay
                with any of them — each auto-swaps to your settlement.
              </Banner>
            )}
          </div>
        )}

        <div
          className="s-label"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Payment routes</span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              textTransform: "none",
              letterSpacing: 0,
              color: "var(--ink-3)",
              fontWeight: 400,
              fontSize: 12,
            }}
          >
            {!loading && !error && (
              <span>Checked {formatRelative(data?.updated_at ?? 0)}</span>
            )}
            <Btn
              variant="ghost"
              onClick={() => void load(true)}
              disabled={refreshing}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </Btn>
          </span>
        </div>

        <div className="card">
          {loading ? (
            <div className="card-pad" style={{ color: "var(--ink-3)" }}>
              Loading…
            </div>
          ) : warming ? (
            <div className="empty">
              <h4>Route monitoring is starting up</h4>
              <p>
                The first health sweep runs moments after the service boots.
                Check back in a few seconds.
              </p>
            </div>
          ) : routes.length === 0 ? (
            <div className="empty">
              <h4>No routes to report</h4>
              <p>
                Swap-route health appears here once "pay with any crypto" is
                live on the backend.
              </p>
            </div>
          ) : (
            sorted.map((r) => {
              const st = stateOf(r);
              const meta = STATE_META[st];
              const protoLabel = r.protocol
                ? (PROTOCOL_LABEL[r.protocol] ?? r.protocol)
                : null;
              return (
                <div key={r.asset_id} className="li">
                  <div className="li-main">
                    <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                      {r.label}
                    </div>
                    <div className="li-meta">
                      <span className="mono">{r.ticker}</span>
                      {st === "down"
                        ? " · Temporarily unavailable"
                        : protoLabel
                          ? ` · via ${protoLabel}`
                          : ""}
                      {st === "recovering" && r.last_healthy
                        ? ` · last live ${formatRelative(r.last_healthy)}`
                        : ""}
                    </div>
                  </div>
                  <div className="li-right">
                    <span className={meta.pill}>
                      <span className="pd" />
                      {meta.label}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {!loading && !error && !warming && routes.length > 0 && (
          <div className="help" style={{ marginTop: 12 }}>
            Routes are checked against Base USDC every 5 minutes. A coin is only
            hidden from checkout after it fails two checks in a row, so a brief
            network blip never pulls a coin offline.
          </div>
        )}
      </div>
    </div>
  );
}
